import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Label } from "@/core/components/ui/label";
import { Switch } from "@/core/components/ui/switch";
import { Textarea } from "@/core/components/ui/textarea";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreateProductProviderMapping } from "@/features/ledger/api/use-create-product-provider-mapping";
import type { ProductProviderMappingDto } from "@/features/ledger/api/dto";
import { useLedgerTranslation } from "@/features/ledger/i18n/use-ledger-translation";
import {
  createProductProviderMappingSchema,
  type ProductProviderMappingFormValues,
} from "@/features/ledger/schemas/product-provider-mapping.schema";
import {
  useProductCodeSelectOptions,
  useProviderSelectOptions,
} from "@/features/shared/api";

const EMPTY_VALUES: ProductProviderMappingFormValues = {
  productCodeId: "",
  providerId: "",
  externalConfig: "",
  isActive: true,
};

const toPayload = (
  values: ProductProviderMappingFormValues
): ProductProviderMappingDto => ({
  productCodeId: values.productCodeId.trim(),
  providerId: values.providerId.trim(),
  externalConfig: values.externalConfig?.trim() || null,
  isActive: values.isActive,
});

/**
 * Formulaire d'association d'un code produit à un provider avec sa
 * configuration d'intégration (`POST /ProductProviderMappings/add`,
 * guide §6.4) — réservé à flashpay-superadmin / flashpay-admin. Preview
 * ShareablePreview avant submit.
 */
export function ProductProviderMappingFormDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLedgerTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : reconstruit le schéma zod traduit à chaque changement de langue.
  const schema = useMemo(() => createProductProviderMappingSchema(), [i18n.language]);

  const create = useCreateProductProviderMapping();
  const [preview, setPreview] = useState<ProductProviderMappingDto | null>(
    null
  );
  const { options: productCodeOptions, isLoading: productCodesLoading } =
    useProductCodeSelectOptions(open);
  const { options: providerOptions, isLoading: providersLoading } =
    useProviderSelectOptions(open);

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  const handleConfirm = () => {
    if (!preview) return;
    create.mutate(preview, {
      onSuccess: () => {
        notifSilence(t("ledger.mappings.feedback.created"));
        setPreview(null);
        onOpenChange(false);
      },
      onError: (error) => notifSilenceError(error.message),
    });
  };

  const fieldError =
    (name: keyof ProductProviderMappingFormValues) =>
    (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("ledger.mappings.create")}</DialogTitle>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field
              name="productCodeId"
              validators={{ onChange: fieldError("productCodeId") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="productCodeId">
                    {t("ledger.mappings.form.productCodeId")}
                  </Label>
                  <EntitySearchableSelect
                    id="productCodeId"
                    options={productCodeOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={t(
                      "ledger.mappings.form.productCodeIdPlaceholder"
                    )}
                    isLoading={productCodesLoading}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="providerId"
              validators={{ onChange: fieldError("providerId") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="providerId">
                    {t("ledger.mappings.form.providerId")}
                  </Label>
                  <EntitySearchableSelect
                    id="providerId"
                    options={providerOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={t("ledger.mappings.form.providerIdPlaceholder")}
                    isLoading={providersLoading}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="externalConfig">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="externalConfig">
                    {t("ledger.mappings.form.externalConfig")}
                  </Label>
                  <Textarea
                    id="externalConfig"
                    className="notranslate font-mono text-xs"
                    placeholder="{}"
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="isActive">
              {(field) => (
                <div className="flex items-center gap-2">
                  <Switch
                    id="isActive"
                    checked={field.state.value}
                    onCheckedChange={(v) => field.handleChange(v)}
                  />
                  <Label htmlFor="isActive">
                    {t("ledger.mappings.form.isActive")}
                  </Label>
                </div>
              )}
            </form.Field>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={create.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? <ButtonSpinner /> : null}
                {t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {preview ? (
        <ShareablePreview
          data={preview}
          title={t("ledger.mappings.preview.createTitle")}
          description={t("ledger.mappings.preview.createDescription")}
          mode="user"
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !create.isPending) setPreview(null);
          }}
          maxHeight="90vh"
          actions={createPreviewActions({
            onConfirm: handleConfirm,
            onCancel: () => setPreview(null),
            t,
            confirmLoading: create.isPending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </>
  );
}
